import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import { makeStyles } from "tss-react/mui";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import CloseIcon from "@mui/icons-material/Close";

import { ButtonUI, KMKDialog } from "../../../Common";
import FirstStepComponent from "./Stepper/FirstStepComponent";
import SecondStepComponent from "./Stepper/SecondStepComponent";
import ThirdStepComponent from "./Stepper/ThirdStepComponent";
import { APPLY_TO_DW_STEPS } from "./ApplyDWData";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiDialogActions-root": {
      padding: "10px 20px 10px 0",
    },
  },
  formContainer: {
    width: "100%",
    textAlign: "left",
    maxHeight: "700px",
    overflow: "auto",
    display: "flex",
    gap: "15px",
    flexDirection: "column",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  closeIcon: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  iconContainer: {
    "& .MuiStepConnector-root": {
      width: "100%",
    },
  },
  [theme.breakpoints.down("510")]: {
    iconContainer: {
      display: "flex",
      flexWrap: "wrap",
      marginLeft: "10px",
    },
  },
}));

const ApplyDwDialog = ({
  applyAll,
  setApplyAll,
  selectionApplyConfirmation,
  setSelectiveApplyConfirmation,
  setModifiedObjects,
  openConfirm,
  setOpenConfirm,
  modifiedObjects = { sources: [], entities: [] },
  handleSelectionPopup,
  handleFirstStep,
  handleSecondStep,
  handleApply,
  loading,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);

  const handleApplyAllChange = useCallback(
    (e) => {
      setApplyAll((prev) => {
        if (prev) {
          setModifiedObjects((prev) => ({
            sources: prev.sources.map((object) => ({ ...object, checked: object.IsDeleted === 1 })),
            entities: prev.entities.map((object) => ({ ...object, checked: object.IsDeleted === 1 })),
          }));
        } else {
          setModifiedObjects((prev) => ({
            sources: prev.sources.map((object) => ({ ...object, checked: true })),
            entities: prev.entities.map((object) => ({ ...object, checked: true })),
          }));
        }
        return !prev;
      });
    },
    [setApplyAll, setModifiedObjects]
  );

  const handleCheckboxCheck = useCallback(
    (e, objectType) => {
      const { checked, value } = e.target;
      setModifiedObjects((prev) => ({
        ...prev,
        [objectType]: prev[objectType].map((object) => (object.id === value ? { ...object, checked } : object)),
      }));
    },
    [setModifiedObjects]
  );

  const handleStepper = async () => {
    if (activeStep === 0) {
      await handleFirstStep();
      setActiveStep((prev) => (applyAll ? prev + 2 : prev + 1));
    } else if (activeStep === 1) {
      await handleSecondStep();
      setActiveStep((prev) => prev + 1);
    } else await handleApply();
  };

  const StepperComponent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return (
          <FirstStepComponent
            applyAll={applyAll}
            handleApplyAllChange={handleApplyAllChange}
            modifiedObjects={modifiedObjects}
            handleCheckboxCheck={handleCheckboxCheck}
          />
        );
      case 1:
        return <SecondStepComponent selectionApplyConfirmation={selectionApplyConfirmation} />;
      case 2:
        return <ThirdStepComponent openConfirm={openConfirm} />;
      default:
        return <ThirdStepComponent openConfirm={openConfirm} />;
    }
  }, [
    activeStep,
    applyAll,
    handleApplyAllChange,
    handleCheckboxCheck,
    modifiedObjects,
    openConfirm,
    selectionApplyConfirmation,
  ]);

  useEffect(() => {
    return () => {
      setOpenConfirm({ columnDeletion: false, tableDeletion: false });
      setSelectiveApplyConfirmation({ sources: [], entities: [] });
      setApplyAll(true);
    };
  }, [dispatch, setApplyAll, setOpenConfirm, setSelectiveApplyConfirmation]);

  const handleStepperCancel = () => {
    if (activeStep === 0) handleSelectionPopup();
    else setActiveStep((prev) => (applyAll ? prev - 2 : prev - 1));
  };

  return (
    <KMKDialog className={classes.dialogPaper} open={true} maxWidth="lg" fullWidth={activeStep === 0}>
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>

      <DialogTitle sx={{ m: 0, p: 0 }}>
        <Paper elevation={3}>
          <Toolbar className={classes.Toolbar}>Apply to Data Warehouse</Toolbar>
          <IconButton className={classes.closeIcon} onClick={handleSelectionPopup}>
            <CloseIcon />
          </IconButton>
        </Paper>
        <Box sx={{ width: "100%", marginTop: "20px", padding: "5px 20px" }}>
          <Stepper activeStep={activeStep} className={classes.iconContainer}>
            {APPLY_TO_DW_STEPS.map(({ id, label }) => (
              <Step key={id}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.formContainer}>
        <StepperComponent />
      </DialogContent>
      <DialogActions>
        <ButtonUI variant="contained" color="secondary" onClick={handleStepperCancel}>
          {activeStep === 0 ? "Cancel" : "Back"}
        </ButtonUI>
        <ButtonUI
          onClick={handleStepper}
          variant="contained"
          color="success"
          style={{ marginLeft: 15 }}
          disabled={
            activeStep === 0 &&
            !applyAll &&
            !modifiedObjects.sources.some(({ checked }) => checked) &&
            !modifiedObjects.entities.some(({ checked }) => checked)
          }
        >
          {[0, 1].includes(activeStep) ? `Next` : `Apply`}
        </ButtonUI>
      </DialogActions>
    </KMKDialog>
  );
};

ApplyDwDialog.propTypes = {
  applyAll: PropTypes.bool.isRequired,
  setApplyAll: PropTypes.func.isRequired,
  selectionApplyConfirmation: PropTypes.object.isRequired,
  setSelectiveApplyConfirmation: PropTypes.func.isRequired,
  setModifiedObjects: PropTypes.func.isRequired,
  openConfirm: PropTypes.object.isRequired,
  setOpenConfirm: PropTypes.func.isRequired,
  modifiedObjects: PropTypes.object.isRequired,
  handleSelectionPopup: PropTypes.func.isRequired,
  handleFirstStep: PropTypes.func.isRequired,
  handleSecondStep: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ApplyDwDialog;
